import React, { useCallback, useEffect, useState } from "react";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { defineMessage, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";

const DAYS = [7, 30, 60, 90];
const NEVER_CONTACTED_KEY = -1;
const CUSTOM_KEY = -2;

const noRecentContactMessage = defineMessage({
  id: "component.quickFilters.noRecentContact.menuLabel",
  defaultMessage: "> {value} {value, plural, one {day} other {days}}",
  description: "No recent contact option label in No Recent Contact filter in Quick filters",
});

const neverContactedLabelMessage = defineMessage({
  id: "component.quickFilters.noRecentContact.menuLabel.never",
  defaultMessage: "Never contacted",
  description: "Never Contacted option in dropdown in NoRecentContact Quick filter",
});

const customLabelMessage = defineMessage({
  id: "component.quickFilters.noRecentContact.menuLabel.custom",
  defaultMessage: "Custom",
  description: "Custom option in dropdown in NoRecentContact Quick filter",
});

interface Props {
  fieldName?: string;
  filter: FilterModel;
  onSetCustomFilters: (customFilters: Partial<FilterModel>) => void;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
}

const NoRecentContactFilter: React.FC<Props> = ({
  fieldName = "noContactDaysOut",
  filter,
  onSetCustomFilters,
  onUpdateFilterModel,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [noRecentContact, setNoRecentContact] = useState<number | undefined>(undefined);

  const handleMenuClick = useCallback(
    ({ key }) => {
      const value = parseInt(key, 10);
      if (value === NEVER_CONTACTED_KEY) {
        onUpdateFilterModel({
          [fieldName]: { operator: FilterOperator.EMPTY, value: undefined },
        });
        analytics.clicked(["No Contact In", "Never"]);
      } else if (value === CUSTOM_KEY) {
        onSetCustomFilters({
          [fieldName]: { operator: FilterOperator.GREATER_THAN, value: undefined },
        });
        analytics.clicked(["No Contact In", "Custom"]);
      } else {
        onUpdateFilterModel({
          [fieldName]: value ? { operator: FilterOperator.GREATER_THAN, value } : undefined,
        });
        analytics.clicked(["No Contact In", `${value} days`]);
      }
    },
    [analytics, fieldName, onSetCustomFilters, onUpdateFilterModel]
  );

  useEffect(() => {
    if (filter && fieldName in filter) {
      const filterCondition = filter[fieldName];
      setNoRecentContact(
        filterCondition.operator === FilterOperator.EMPTY
          ? NEVER_CONTACTED_KEY
          : filterCondition.operator === FilterOperator.GREATER_THAN &&
            DAYS.includes(filterCondition.value)
          ? filterCondition.value
          : undefined
      );
    } else {
      setNoRecentContact(undefined);
    }
  }, [fieldName, filter]);

  return (
    <Dropdown
      overlay={
        <Menu onClick={handleMenuClick}>
          {!!noRecentContact && (
            <Menu.Item key={0}>
              <b>
                {intl.formatMessage({
                  id: "component.quickFilters.noRecentContact.clear",
                  defaultMessage: "Clear filter",
                  description: "Clear option in dropdown in NoRecentContact Quick filter",
                })}
              </b>
            </Menu.Item>
          )}
          {DAYS.map((value) => (
            <Menu.Item key={value}>
              {intl.formatMessage(noRecentContactMessage, { value })}
            </Menu.Item>
          ))}
          <Menu.Item key={NEVER_CONTACTED_KEY}>
            {intl.formatMessage(neverContactedLabelMessage)}
          </Menu.Item>
          <Menu.Item key={CUSTOM_KEY}>{intl.formatMessage(customLabelMessage)}</Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <RoundButton active={!!noRecentContact} hasMenu>
        {intl.formatMessage(
          {
            id: "component.quickFilters.noRecentContact.title",
            defaultMessage:
              "{isNever, select, true {Never contacted} other {No contact in{isActive, select, true {: {value}} other {}}}}",
            description: "No recent contact title",
          },
          {
            isActive: noRecentContact !== undefined,
            isNever: noRecentContact === NEVER_CONTACTED_KEY,
            value:
              noRecentContact !== undefined
                ? intl.formatMessage(noRecentContactMessage, { value: noRecentContact })
                : "",
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default NoRecentContactFilter;
